import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Switch,
  FormControlLabel,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  listInstalls,
  listLicenses,
  InstallSummary,
  License,
  DISK_WARNING_PERCENT,
  DISK_CRITICAL_PERCENT,
} from '../services/onpremApi';
import InstallDetailDrawer from '../components/onprem/InstallDetailDrawer';
import LicenseTable from '../components/onprem/LicenseTable';
import CreateLicenseDialog from '../components/onprem/CreateLicenseDialog';
import RevokeLicenseDialog from '../components/onprem/RevokeLicenseDialog';

type OnPremTab = 'installs' | 'licenses';

// ── Helper functions ──────────────────────────────────────────────────────────

function formatRelativeMinutes(minutes: number): string {
  if (minutes < 1) return 'ahora mismo';
  if (minutes < 60) return `hace ${Math.round(minutes)} min`;
  const hours = minutes / 60;
  if (hours < 24) return `hace ${Math.round(hours)} h`;
  const days = hours / 24;
  return `hace ${Math.round(days)} d`;
}

function diskColor(
  pct: number | null,
): 'success' | 'warning' | 'error' | 'default' {
  if (pct === null || pct === undefined) return 'default';
  if (pct > DISK_CRITICAL_PERCENT) return 'error';
  if (pct > DISK_WARNING_PERCENT) return 'warning';
  return 'success';
}

function healthBadge(
  backendUp: boolean,
  dbUp: boolean,
): { label: string; color: 'success' | 'error' } {
  if (backendUp && dbUp) return { label: 'OK', color: 'success' };
  if (!backendUp && !dbUp) return { label: 'Down', color: 'error' };
  if (!backendUp) return { label: 'Backend Down', color: 'error' };
  return { label: 'DB Down', color: 'error' };
}

// ── Component ─────────────────────────────────────────────────────────────────

const OnPremClients: React.FC = () => {
  const [installs, setInstalls] = useState<InstallSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [endpointUnavailable, setEndpointUnavailable] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'success' | 'error' | 'info',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInstall, setSelectedInstall] = useState<InstallSummary | null>(
    null,
  );
  const [drawerOpen, setDrawerOpen] = useState(false);

  // ── Licenses tab state ──
  const [tab, setTab] = useState<OnPremTab>('installs');
  const [licenses, setLicenses] = useState<License[]>([]);
  const [licensesLoading, setLicensesLoading] = useState(false);
  const [licensesLoaded, setLicensesLoaded] = useState(false);
  const [showRevoked, setShowRevoked] = useState(false);
  const [licenseSearch, setLicenseSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<License | null>(null);

  const loadLicenses = useCallback(async (signal?: AbortSignal) => {
    setLicensesLoading(true);
    try {
      const data = await listLicenses(200, signal);
      setLicenses(data);
      setLicensesLoaded(true);
    } catch (error: any) {
      if (
        error?.name === 'CanceledError' ||
        error?.name === 'AbortError' ||
        error?.code === 'ERR_CANCELED'
      ) {
        return;
      }
      setSnackbar({ open: true, message: 'Error al cargar licencias', severity: 'error' });
    } finally {
      setLicensesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab !== 'licenses' || licensesLoaded) return;
    const controller = new AbortController();
    loadLicenses(controller.signal);
    return () => controller.abort();
  }, [tab, licensesLoaded, loadLicenses]);

  const filteredLicenses = useMemo(() => {
    const q = licenseSearch.toLowerCase().trim();
    return licenses.filter((lic) => {
      if (!showRevoked && lic.status === 'revoked') return false;
      if (!q) return true;
      return (
        lic.client_id.toLowerCase().includes(q) ||
        lic.organization_name.toLowerCase().includes(q) ||
        lic.license_key.toLowerCase().includes(q)
      );
    });
  }, [licenses, licenseSearch, showRevoked]);

  const handleLicenseCreated = (license: License) => {
    setLicenses((prev) => [license, ...prev]);
    setSnackbar({
      open: true,
      message: `Licencia emitida para ${license.client_id}`,
      severity: 'success',
    });
  };

  const handleLicenseRevoked = (license: License) => {
    setLicenses((prev) => prev.map((l) => (l.id === license.id ? license : l)));
    setSnackbar({
      open: true,
      message: `Licencia ${license.license_key} revocada`,
      severity: 'info',
    });
  };

  const handleShowInstall = (license: License) => {
    const install = installs.find((i) => i.install_id === license.install_id);
    if (!install) {
      setSnackbar({
        open: true,
        message: 'Esa instalación aún no ha enviado beacons en los últimos 30 días',
        severity: 'info',
      });
      return;
    }
    setTab('installs');
    setSelectedInstall(install);
    setDrawerOpen(true);
  };

  const loadInstalls = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setEndpointUnavailable(false);
    try {
      const data = await listInstalls(200, signal);
      setInstalls(data);
    } catch (error: any) {
      // Abort-related errors are not user-visible failures.
      if (
        error?.name === 'CanceledError' ||
        error?.name === 'AbortError' ||
        error?.code === 'ERR_CANCELED'
      ) {
        return;
      }
      const status = error?.response?.status;
      if (status === 404 || status === 501 || status === 422) {
        setEndpointUnavailable(true);
      } else {
        setSnackbar({
          open: true,
          message: 'Error al cargar instalaciones on-prem',
          severity: 'error',
        });
        setEndpointUnavailable(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadInstalls(controller.signal);
    return () => controller.abort();
  }, [loadInstalls]);

  const filteredInstalls = useMemo(() => {
    if (!searchTerm.trim()) return installs;
    const q = searchTerm.toLowerCase().trim();
    return installs.filter(
      (inst) =>
        inst.client_id.toLowerCase().includes(q) ||
        (inst.fleet_manager_version ?? '').toLowerCase().includes(q) ||
        (inst.host_os ?? '').toLowerCase().includes(q),
    );
  }, [installs, searchTerm]);

  const handleRowClick = (install: InstallSummary) => {
    setSelectedInstall(install);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedInstall(null);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Page header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1">
            Clientes On-Prem
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {tab === 'installs'
              ? 'Instalaciones activas en los últimos 30 días'
              : 'Licencias emitidas para instalaciones on-prem'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {tab === 'installs' ? (
            <Chip
              label={`${installs.length} instalación${installs.length !== 1 ? 'es' : ''}`}
              color="primary"
              variant="outlined"
            />
          ) : (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
            >
              Emitir licencia
            </Button>
          )}
          <Tooltip title="Actualizar">
            <span>
              <IconButton
                onClick={() => (tab === 'installs' ? loadInstalls() : loadLicenses())}
                disabled={tab === 'installs' ? loading : licensesLoading}
                color="primary"
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_e, v: OnPremTab) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="installs" label="Instalaciones" />
        <Tab value="licenses" label="Licencias" />
      </Tabs>

      {endpointUnavailable && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Esta función requiere actualización del backend
        </Alert>
      )}

      {tab === 'installs' && (
        <>
      {/* Search box */}
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar por cliente, versión u OS..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Main table */}
      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Versión</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Último beacon</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Disco</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Agentes</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                Beacons (30d)
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Host OS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  Cargando...
                </TableCell>
              </TableRow>
            ) : filteredInstalls.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  {endpointUnavailable
                    ? 'El endpoint on-prem aún no está disponible.'
                    : searchTerm
                      ? 'No se encontraron instalaciones con ese criterio de búsqueda.'
                      : 'No hay instalaciones on-prem activas'}
                </TableCell>
              </TableRow>
            ) : (
              filteredInstalls.map((inst) => {
                const health = healthBadge(inst.backend_up, inst.db_up);
                const agentMismatch = inst.agents_connected < inst.agents_configured;
                return (
                  <TableRow
                    key={inst.install_id}
                    hover
                    onClick={() => handleRowClick(inst)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRowClick(inst);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`Ver detalle de ${inst.client_id}`}
                    sx={{ cursor: 'pointer' }}
                  >
                    {/* Cliente */}
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {inst.client_id}
                    </TableCell>

                    {/* Versión */}
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {inst.fleet_manager_version ?? '—'}
                    </TableCell>

                    {/* Último beacon */}
                    <TableCell>
                      <Tooltip
                        title={new Date(inst.last_beacon_at).toLocaleString(
                          'es-PA',
                        )}
                      >
                        <span>
                          {formatRelativeMinutes(inst.minutes_since_last_beacon)}
                        </span>
                      </Tooltip>
                    </TableCell>

                    {/* Disco */}
                    <TableCell>
                      {inst.disk_percent_used !== null &&
                      inst.disk_percent_used !== undefined ? (
                        <Chip
                          label={`${inst.disk_percent_used.toFixed(1)}%`}
                          color={diskColor(inst.disk_percent_used)}
                          size="small"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>

                    {/* Estado */}
                    <TableCell>
                      <Chip
                        label={health.label}
                        color={health.color}
                        size="small"
                      />
                    </TableCell>

                    {/* Agentes */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: agentMismatch ? 'warning.main' : 'inherit',
                          fontWeight: agentMismatch ? 700 : 400,
                        }}
                      >
                        {inst.agents_connected}/{inst.agents_configured}
                      </Typography>
                    </TableCell>

                    {/* Beacons 30d */}
                    <TableCell align="right">{inst.total_beacons}</TableCell>

                    {/* Host OS */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {inst.host_os ?? '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
        </>
      )}

      {tab === 'licenses' && (
        <>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
            <TextField
              size="small"
              placeholder="Buscar por cliente, organización o clave..."
              value={licenseSearch}
              onChange={(e) => setLicenseSearch(e.target.value)}
              sx={{ width: 400 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showRevoked}
                  onChange={(e) => setShowRevoked(e.target.checked)}
                />
              }
              label="Mostrar revocadas"
            />
            {licensesLoading && <CircularProgress size={20} />}
          </Box>
          <LicenseTable
            licenses={filteredLicenses}
            onRevoke={(lic) => setRevokeTarget(lic)}
            onShowInstall={handleShowInstall}
          />
        </>
      )}

      <CreateLicenseDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleLicenseCreated}
      />
      <RevokeLicenseDialog
        license={revokeTarget}
        open={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        onRevoked={handleLicenseRevoked}
      />

      {/* Detail drawer */}
      <InstallDetailDrawer
        install={selectedInstall}
        open={drawerOpen}
        onClose={handleDrawerClose}
      />

      {/* Snackbar for errors */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OnPremClients;
